// The read-only TipTap-JSON renderer (F-VERSIONS-PREVIEW). Walks a section's
// `content_json` (a ProseMirror doc) into React, mirroring the proven web FE
// node handling (`theplaidoyer-frontend/src/utils/tiptap.ts`): doc / paragraph
// / heading(level) / text(+bold/italic marks) / hardBreak, plus the inline
// `citationRef` atom node and the `citation` text mark.
//
// W1 — pure presentation: it renders BE truth, computes no legal state.
// W2 — ZERO Office.js. This is the explicitly NON-Office.js half of versions.
// W7 — a `citationRef` resolves to a chip ONLY when its refKey is in the
//      response-attached registry. An unregistered refKey degrades to plain
//      text (exactly like `CitationChips` leaves an unbacked `[REF:x]` alone)
//      — the add-in never fabricates a citation.
//
// §9 — logical RTL utilities only; every BE text run gets per-string direction
//      via `bidiProps` (content can be Arabic in an FR chrome and vice-versa).
//      Unknown node types degrade gracefully (render their text children, or
//      skip) — never throw.

import { bidiProps } from '@/lib/bidi'
import type {
  TipTapDoc,
  TipTapNode,
  TipTapMark,
  CitationRef,
} from '@/domain/ecriture/entity'

interface SectionRendererProps {
  doc: TipTapDoc
  /** Resolved citation registry (refKey → entry). A `citationRef` not present
   *  here renders as plain text, never a fabricated chip (W7). */
  registry: Record<string, CitationRef>
}

/** Render a section's TipTap document read-only. Top-level block nodes are
 *  walked in order; everything is presentation (W1/W2). */
export function SectionRenderer({
  doc,
  registry,
}: SectionRendererProps): JSX.Element {
  return (
    <div
      data-testid="section-renderer"
      className="flex flex-col gap-2 text-xs leading-relaxed text-ink/80"
    >
      {doc.content.map((node, i) => (
        <BlockNode key={i} node={node} registry={registry} />
      ))}
    </div>
  )
}

interface BlockNodeProps {
  node: TipTapNode
  registry: Record<string, CitationRef>
}

/** Render one block-level node. Known: paragraph + heading(level). Unknown
 *  block types degrade to a paragraph of their inline children (never throw). */
function BlockNode({ node, registry }: BlockNodeProps): JSX.Element | null {
  const children = <InlineChildren nodes={node.content} registry={registry} />

  if (node.type === 'heading') {
    const level = headingLevel(node.attrs)
    const cls =
      level <= 1
        ? 'text-sm font-bold text-ink'
        : level === 2
          ? 'text-xs font-bold text-ink'
          : 'text-xs font-semibold text-ink'
    // Render via aria role rather than a dynamic tag so the read-only preview
    // stays a flat, copy-safe block (no h1..h6 document outline to manage).
    return (
      <div role="heading" aria-level={level} className={cls}>
        {children}
      </div>
    )
  }

  // paragraph + any unknown block → a paragraph of inline children. A block
  // with no inline content yields an empty paragraph (preserves spacing).
  return <p>{children}</p>
}

/** Clamp a heading node's `attrs.level` into the 1..6 range; default 1. */
function headingLevel(attrs: Record<string, unknown> | undefined): number {
  const raw = attrs?.level
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.min(6, Math.max(1, Math.trunc(raw)))
  }
  return 1
}

interface InlineChildrenProps {
  nodes: TipTapNode[] | undefined
  registry: Record<string, CitationRef>
}

/** Render the inline run of a block: text(+marks), hardBreak, citationRef. */
function InlineChildren({
  nodes,
  registry,
}: InlineChildrenProps): JSX.Element | null {
  if (!Array.isArray(nodes) || nodes.length === 0) return null
  return (
    <>
      {nodes.map((node, i) => (
        <InlineNode key={i} node={node} registry={registry} />
      ))}
    </>
  )
}

interface InlineNodeProps {
  node: TipTapNode
  registry: Record<string, CitationRef>
}

/** Render one inline node. */
function InlineNode({ node, registry }: InlineNodeProps): JSX.Element | null {
  if (node.type === 'hardBreak') return <br />

  if (node.type === 'citationRef') {
    const refKey = readRefKey(node.attrs)
    // W7 — resolve from the registry ONLY, and ONLY via an OWN property. A bare
    // `registry[refKey]` lets a refKey like `toString`/`constructor` resolve to
    // an inherited `Object.prototype` member (truthy) and forge a chip for a
    // citation the BE never shipped. A miss → plain text (`[REF:key]`), never a
    // fabricated chip. (Defense-in-depth with the null-proto map in case-http.)
    const ref =
      refKey && Object.prototype.hasOwnProperty.call(registry, refKey)
        ? registry[refKey]
        : undefined
    if (ref) return <CitationRefChip refKey={ref.refKey} entry={ref} />
    return refKey ? <span>{`[REF:${refKey}]`}</span> : null
  }

  if (typeof node.text === 'string') {
    return <TextRun text={node.text} marks={node.marks} />
  }

  // Unknown inline node with nested content → render its text children; a leaf
  // unknown node with no text/content → skip. Degrade gracefully, never throw.
  if (Array.isArray(node.content)) {
    return <InlineChildren nodes={node.content} registry={registry} />
  }
  return null
}

interface TextRunProps {
  text: string
  marks: TipTapMark[] | undefined
}

/** A text run with its bold/italic marks applied + per-string direction. The
 *  `citation` mark (BE-verified inline citation) is rendered as a subtle
 *  underline — it does not gate on the registry (it is itself BE truth). */
function TextRun({ text, marks }: TextRunProps): JSX.Element {
  const types = new Set(
    (marks ?? [])
      .map((m) => m.type)
      .filter((t): t is string => typeof t === 'string'),
  )
  const className = [
    types.has('bold') ? 'font-semibold' : '',
    types.has('italic') ? 'italic' : '',
    types.has('citation') ? 'underline decoration-dotted underline-offset-2' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={className || undefined} {...bidiProps(text)}>
      {text}
    </span>
  )
}

interface CitationRefChipProps {
  refKey: string
  entry: CitationRef
}

/** A read-only citation chip resolved from the registry. Reuses the visual
 *  pattern of `agent/CitationChips` (gold dot + `bidiProps(label)`). The label
 *  is BE-supplied; it falls back to the refKey, never blank, never invented. */
function CitationRefChip({
  refKey,
  entry,
}: CitationRefChipProps): JSX.Element {
  const label = chipLabel(entry)
  return (
    <span
      data-testid={`section-citation-chip-${refKey}`}
      className="mx-0.5 inline-flex items-baseline gap-1 rounded bg-gold/10 px-1.5 py-0.5 text-[11px] leading-snug text-ink/80 align-baseline"
    >
      <span
        aria-hidden="true"
        className="inline-block h-1.5 w-1.5 shrink-0 self-center rounded-full bg-gold"
      />
      <span className="font-medium" {...bidiProps(label)}>
        {label}
      </span>
    </span>
  )
}

/** Compose the chip label from the BE-supplied parts. Falls back to the
 *  canonical refKey when the human-readable parts are absent — never blank,
 *  never fabricated (mirrors `agent/CitationChips::chipLabel`). */
function chipLabel(entry: CitationRef): string {
  const parts = [entry.codeName, entry.articleNumber].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  )
  return parts.length > 0 ? parts.join(' ') : entry.refKey
}

/** Read a `citationRef` node's refKey (BE attr `refKey`). `null` when absent. */
function readRefKey(attrs: Record<string, unknown> | undefined): string | null {
  const v = attrs?.refKey
  return typeof v === 'string' && v.length > 0 ? v : null
}
